import { ReactNode } from 'react';
import { useTranslation } from '@frontend/i18n';
import { LocationChips } from '@frontend/location-chips';
import { theme } from '@frontend/theme';
import { BaseFieldProps, Heading, Modal, ModalControlModalProps, SwitchField, Text } from '@frontend/design-system';

const ModalBody = ({ children, ...rest }: { children: ReactNode }) => <Modal.Body {...rest}>{children}</Modal.Body>;

const ModalToggle = ({
  fieldProps,
  indeterminate,
  label,
}: {
  fieldProps: BaseFieldProps<boolean, '', HTMLInputElement>;
  indeterminate: boolean;
  label: string;
}) => {
  return (
    <div
      css={{
        display: 'flex',
        padding: theme.spacing(1),
        alignItems: 'center',
        justifyContent: 'space-between',
        border: `1px solid ${theme.colors.neutral20}`,
        borderRadius: theme.borderRadius.small,
      }}
    >
      <Text>{label}</Text>
      <SwitchField {...fieldProps} label='' name='toggle-all' indeterminate={indeterminate} />
    </div>
  );
};

const ModalFooter = ({
  disabledPrimary,
  helperText,
  onCancel,
  onSave,
}: {
  disabledPrimary?: boolean;
  helperText?: string;
  onCancel: () => void;
  onSave: () => void;
}) => {
  const { t } = useTranslation('messages');

  return (
    <Modal.Footer
      helperText={helperText}
      primary={{
        disabled: disabledPrimary,
        label: t('Save'),
        onClick: onSave,
      }}
      secondary={{
        label: t('Cancel'),
        onClick: onCancel,
      }}
    />
  );
};

type Props = {
  children: ReactNode;
  filteredLocationIds: string[];
  modalProps: ModalControlModalProps;
  title: string;
};

export const ManageAllModal = ({ children, filteredLocationIds, modalProps, title, ...rest }: Props) => (
  <Modal {...modalProps} minWidth={600} {...rest}>
    <Modal.Header onClose={modalProps.onClose}>
      <div css={{ display: 'flex', gap: theme.spacing(1) }}>
        <Heading level={2}>{title}</Heading>
        <LocationChips filteredLocationIds={filteredLocationIds} />
      </div>
    </Modal.Header>
    {children}
  </Modal>
);

ManageAllModal.Body = Object.assign(ModalBody, {
  Toggle: ModalToggle,
});
ManageAllModal.Footer = ModalFooter;
